/**
 * @module AdapterCard
 * @package @geenius/adapters/solidjs-ark
 * @description Ark-native SolidJS adapter summary card for a single adapter
 * domain and provider status.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
} from "@geenius/adapters";
import type { JSX } from "solid-js";
import { Show } from "solid-js";
import { classNames } from "../lib/classNames";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for rendering a single Ark Solid adapter summary card.
 *
 * @property domain Adapter domain represented by the card.
 * @property status Current provider health and metadata for the domain.
 * @property onClick Optional callback fired when the card is activated.
 * @property class Optional class name merged into the card container.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  class?: string;
}

/** Renders an adapter domain summary with Ark variant data attributes. */
export function AdapterCard(props: AdapterCardProps): JSX.Element {
  const label = () =>
    `${DOMAIN_LABELS[props.domain]} adapter - ${props.status.status}`;
  const className = () =>
    classNames(
      "group relative flex w-full items-start gap-4 overflow-hidden rounded-md border border-slate-200 bg-white p-5 text-left text-slate-950 shadow-sm transition-colors",
      props.onClick
        ? "hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40"
        : "cursor-default",
      props.class,
    );

  const content = () => (
    <>
      <div
        aria-hidden="true"
        class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-100 text-2xl shadow-sm transition-transform duration-300 group-hover:scale-105"
      >
        {DOMAIN_ICONS[props.domain]}
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center justify-between gap-2">
          <h3 class="truncate text-sm font-bold text-slate-950">
            {DOMAIN_LABELS[props.domain]}
          </h3>
          <AdapterStatusBadge status={props.status.status} />
        </div>
        <p class="mt-0.5 line-clamp-2 text-xs text-slate-500">
          {DOMAIN_DESCRIPTIONS[props.domain]}
        </p>
        <Show when={props.status.provider && props.status.provider !== "none"}>
          <div class="mt-2 flex items-center gap-2">
            <span class="rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[0.625rem] font-semibold text-slate-600">
              {props.status.provider}
            </span>
            <Show when={props.status.latency !== undefined}>
              <span class="text-[0.625rem] text-slate-500">
                {props.status.latency}ms
              </span>
            </Show>
          </div>
        </Show>
        <Show when={props.status.error}>
          <p class="mt-1.5 truncate text-[0.625rem] text-red-700">
            {props.status.error}
          </p>
        </Show>
      </div>
    </>
  );

  if (!props.onClick) {
    return (
      <article
        aria-label={label()}
        class={className()}
        data-gn-adapters-ark-component="adapter-card"
        data-status={props.status.status}
      >
        {content()}
      </article>
    );
  }

  return (
    <button
      aria-label={label()}
      class={className()}
      data-gn-adapters-ark-component="adapter-card"
      data-status={props.status.status}
      onClick={props.onClick}
      type="button"
    >
      {content()}
    </button>
  );
}
